/**
 * One-Time Job Service
 * Handles OneTimeJobs collection operations for web dashboard
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { OneTimeJob } from '../../../shared/types';
import { db, functions } from '../config/firebase';
import { convertDocumentTimestamps } from '@shared/utils/firestore';

export interface OneTimeJobWithId extends OneTimeJob {
  id: string;
}

export interface CreateOneTimeJobInput {
  companyId: string;
  title: string;
  description?: string;
  location: {
    address: string;
    latitude: number;
    longitude: number;
  };
  scheduledStart: Date;
  scheduledEnd: Date;
  hourlyRate: number;
}

/**
 * Firestore collection name for one-time jobs
 */
export const ONE_TIME_JOBS_COLLECTION = 'OneTimeJobs';

function normalizeOneTimeJob(id: string, data: Record<string, unknown>): OneTimeJobWithId {
  const converted = convertDocumentTimestamps<OneTimeJob>(data);
  const userIds: string[] = converted.userIds ?? (converted.userId ? [converted.userId] : []);
  return { ...converted, id, userIds } as OneTimeJobWithId;
}

/**
 * Get all one-time jobs for a company
 */
export const getCompanyOneTimeJobs = async (companyId: string): Promise<OneTimeJobWithId[]> => {
  const q = query(
    collection(db, ONE_TIME_JOBS_COLLECTION),
    where('companyId', '==', companyId),
    orderBy('scheduledStart', 'desc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => normalizeOneTimeJob(doc.id, doc.data()));
};

export const getManagerOneTimeJobs = async (managerId: string): Promise<OneTimeJobWithId[]> => {
  const q = query(
    collection(db, ONE_TIME_JOBS_COLLECTION),
    where('assignedManagerId', '==', managerId),
    orderBy('scheduledStart', 'desc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => normalizeOneTimeJob(doc.id, doc.data()));
};

/**
 * Get active (non-cancelled) one-time jobs for a company
 */
export const getActiveOneTimeJobs = async (companyId: string): Promise<OneTimeJobWithId[]> => {
  const q = query(
    collection(db, ONE_TIME_JOBS_COLLECTION),
    where('companyId', '==', companyId),
    where('status', '!=', 'cancelled'),
    orderBy('scheduledStart', 'desc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => normalizeOneTimeJob(doc.id, doc.data()));
};

/**
 * Get a single one-time job by ID
 */
export const getOneTimeJobById = async (oneTimeJobId: string): Promise<OneTimeJobWithId | null> => {
  const docRef = doc(db, ONE_TIME_JOBS_COLLECTION, oneTimeJobId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return normalizeOneTimeJob(docSnap.id, docSnap.data());
};

/**
 * Create a new one-time job (via Cloud Function)
 */
export const createOneTimeJob = async (data: {
  companyId: string;
  title: string;
  description?: string;
  location: {
    address: string;
    latitude: number;
    longitude: number;
  };
  scheduledStart: Date;
  scheduledEnd: Date;
  hourlyRate: number;
}): Promise<string> => {
  const createFn = httpsCallable(functions, 'createOneTimeJob');
  const result = await createFn({
    ...data,
    scheduledStart: { seconds: Math.floor(data.scheduledStart.getTime() / 1000), nanoseconds: 0 },
    scheduledEnd: { seconds: Math.floor(data.scheduledEnd.getTime() / 1000), nanoseconds: 0 },
  });
  return (result.data as { oneTimeJobId: string }).oneTimeJobId;
};

/**
 * Update a one-time job (via Cloud Function)
 */
export const updateOneTimeJob = async (data: {
  oneTimeJobId: string;
  companyId: string;
  title?: string;
  description?: string;
  location?: {
    address: string;
    latitude: number;
    longitude: number;
  };
  scheduledStart?: Date;
  scheduledEnd?: Date;
  hourlyRate?: number;
}): Promise<void> => {
  const updateFn = httpsCallable(functions, 'updateOneTimeJob');
  await updateFn({
    ...data,
    scheduledStart: data.scheduledStart
      ? { seconds: Math.floor(data.scheduledStart.getTime() / 1000), nanoseconds: 0 }
      : undefined,
    scheduledEnd: data.scheduledEnd
      ? { seconds: Math.floor(data.scheduledEnd.getTime() / 1000), nanoseconds: 0 }
      : undefined,
  });
};

/**
 * Cancel a one-time job (via Cloud Function)
 * If companyId is not provided, it will be fetched from the one-time job
 */
export const cancelOneTimeJob = async (
  oneTimeJobId: string,
  companyId?: string
): Promise<void> => {
  let resolvedCompanyId = companyId;
  if (!resolvedCompanyId) {
    const oneTimeJob = await getOneTimeJobById(oneTimeJobId);
    if (!oneTimeJob) {
      throw new Error('One-time job not found');
    }
    resolvedCompanyId = oneTimeJob.companyId;
  }
  const cancelFn = httpsCallable(functions, 'cancelOneTimeJob');
  await cancelFn({ oneTimeJobId, companyId: resolvedCompanyId });
};

/**
 * Assign one-time job to manager or staff (via Cloud Function)
 */
export const assignOneTimeJob = async (data: {
  oneTimeJobId: string;
  companyId: string;
  assignedManagerId?: string;
  userIds?: string[];
}): Promise<void> => {
  const assignFn = httpsCallable(functions, 'assignOneTimeJob');
  await assignFn(data);
};

/**
 * Unassign a one-time job (via Cloud Function)
 * If companyId is not provided, it will be fetched from the one-time job
 */
export const unassignOneTimeJob = async (
  oneTimeJobId: string,
  companyId?: string
): Promise<void> => {
  let resolvedCompanyId = companyId;
  if (!resolvedCompanyId) {
    const oneTimeJob = await getOneTimeJobById(oneTimeJobId);
    if (!oneTimeJob) {
      throw new Error('One-time job not found');
    }
    resolvedCompanyId = oneTimeJob.companyId;
  }
  const unassignFn = httpsCallable(functions, 'unassignOneTimeJob');
  await unassignFn({ oneTimeJobId, companyId: resolvedCompanyId });
};

/**
 * Remove a single staff member from a one-time job (via Cloud Function)
 */
export const removeStaffFromJob = async (
  oneTimeJobId: string,
  companyId: string,
  staffId: string,
): Promise<void> => {
  const fn = httpsCallable(functions, 'removeStaffFromJob');
  await fn({ oneTimeJobId, companyId, staffId });
};

/**
 * Manager assigns one-time job to one of their staff members (via Cloud Function)
 */
export const managerAssignOneTimeJobToStaff = async (
  oneTimeJobId: string,
  staffId: string,
  managerId: string
): Promise<void> => {
  const assignFn = httpsCallable(functions, 'assignOneTimeJobToStaff');
  // Get companyId from the one-time job
  const oneTimeJob = await getOneTimeJobById(oneTimeJobId);
  if (!oneTimeJob) {
    throw new Error('One-time job not found');
  }
  await assignFn({ oneTimeJobId, staffId, companyId: oneTimeJob.companyId, managerId });
};

/**
 * Assign one-time job to manager (wrapper for company admin use)
 */
export const assignOneTimeJobToManager = async (
  oneTimeJobId: string,
  managerId: string
): Promise<void> => {
  const oneTimeJob = await getOneTimeJobById(oneTimeJobId);
  if (!oneTimeJob) {
    throw new Error('One-time job not found');
  }
  const assignFn = httpsCallable(functions, 'assignOneTimeJob');
  await assignFn({
    oneTimeJobId,
    companyId: oneTimeJob.companyId,
    assignedManagerId: managerId,
  });
};

/**
 * Assign one-time job to staff (wrapper for company admin use)
 * Adds staffId to the existing userIds array.
 */
export const assignOneTimeJobToStaff = async (
  oneTimeJobId: string,
  staffId: string
): Promise<void> => {
  const oneTimeJob = await getOneTimeJobById(oneTimeJobId);
  if (!oneTimeJob) {
    throw new Error('One-time job not found');
  }
  const existingUserIds = oneTimeJob.userIds ?? [];
  if (existingUserIds.includes(staffId)) return;
  const assignFn = httpsCallable(functions, 'assignOneTimeJob');
  await assignFn({
    oneTimeJobId,
    userIds: [...existingUserIds, staffId],
    companyId: oneTimeJob.companyId,
  });
};
